import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Settings, Trophy, Users, Calendar, Target, Plus, X } from 'lucide-react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

const CreateLeaguePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    season: '',
    startDate: '',
    endDate: '',
    description: '',
    isActive: false
  });

  const [matchRules, setMatchRules] = useState({
    setsPerMatch: 3,
    standardSets: 2,
    superTiebreaker: true,
    tiebreakerPoints: 10,
    matchDuration: 60,
    courtCount: 2
  });

  const [pointsSystem, setPointsSystem] = useState({
    winAllSets: 4,
    winTwoLoseOne: 3,
    loseTwoWinOne: 1,
    loseAll: 0,
    tiebreak: 1
  });

  const [tierRules, setTierRules] = useState([
    { tier: 'Tier 1', canPlay: ['Seed 1', 'Seed 2'], color: '#FFD700' },
    { tier: 'Tier 2', canPlay: ['Seed 1', 'Seed 2', 'Seed 3', 'Seed 4'], color: '#C0C0C0' },
    { tier: 'Tier 3', canPlay: ['Seed 3', 'Seed 4'], color: '#CD7F32' }
  ]);

  const [teamConstraints, setTeamConstraints] = useState({
    minUnder18: 1,
    maxUnder18: 2,
    minOver40: 1,
    maxOver40: 2,
    totalPlayers: 12
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();

  const steps = [
    { id: 1, title: 'Basic Info', icon: Trophy },
    { id: 2, title: 'Match Rules', icon: Target },
    { id: 3, title: 'Points System', icon: Settings },
    { id: 4, title: 'Tier Rules', icon: Users },
    { id: 5, title: 'Team Constraints', icon: Calendar }
  ];

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) : value
    }));
  };

  const handleMatchRulesChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMatchRules(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value)
    }));
  };

  const handlePointsSystemChange = (e) => {
    const { name, value } = e.target;
    setPointsSystem(prev => ({
      ...prev,
      [name]: parseInt(value)
    }));
  };

  const handleTierRuleChange = (index, field, value) => {
    const newTierRules = [...tierRules];
    newTierRules[index] = { ...newTierRules[index], [field]: value };
    setTierRules(newTierRules);
  };

  const addTierRule = () => {
    setTierRules([...tierRules, { tier: '', canPlay: [], color: '#6B7280' }]);
  };

  const removeTierRule = (index) => {
    setTierRules(tierRules.filter((_, i) => i !== index));
  };

  const handleTeamConstraintsChange = (e) => {
    const { name, value } = e.target;
    setTeamConstraints(prev => ({
      ...prev,
      [name]: parseInt(value)
    }));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const leagueData = {
        ...formData,
        matchRules,
        pointsSystem,
        tierRules,
        teamConstraints,
        createdAt: new Date().toISOString(),
        status: 'draft'
      };

      const leagueRef = await addDoc(collection(db, 'leagues'), leagueData);
      
      // Create league configuration document
      await addDoc(collection(db, 'leagueConfigs'), {
        leagueId: leagueRef.id,
        ...leagueData,
        updatedAt: new Date().toISOString()
      });

      navigate('/league');
    } catch (error) {
      setError('Failed to create league. Please try again.');
      console.error('Error creating league:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#1C563D' }}>
              Basic League Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  League Name *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  className="form-input"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., OffCourt Premier League"
                />
              </div>

              <div className="form-group">
                <label htmlFor="season" className="form-label">
                  Season *
                </label>
                <input
                  id="season"
                  name="season"
                  type="text"
                  required
                  className="form-input"
                  value={formData.season}
                  onChange={handleChange}
                  placeholder="e.g., 2024 Spring"
                />
              </div>

              <div className="form-group">
                <label htmlFor="startDate" className="form-label">
                  Start Date *
                </label>
                <input
                  id="startDate"
                  name="startDate"
                  type="date"
                  required
                  className="form-input"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate" className="form-label">
                  End Date *
                </label>
                <input
                  id="endDate"
                  name="endDate"
                  type="date"
                  required
                  className="form-input"
                  value={formData.endDate}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="form-input"
                rows="4"
                value={formData.description}
                onChange={handleChange}
                placeholder="League description and special rules..."
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#1C563D' }}>
              Match Rules Configuration
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="setsPerMatch" className="form-label">
                  Sets Per Match *
                </label>
                <input
                  id="setsPerMatch"
                  name="setsPerMatch"
                  type="number"
                  min="1"
                  max="5"
                  className="form-input"
                  value={matchRules.setsPerMatch}
                  onChange={handleMatchRulesChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="standardSets" className="form-label">
                  Standard Sets
                </label>
                <input
                  id="standardSets"
                  name="standardSets"
                  type="number"
                  min="1"
                  max="3"
                  className="form-input"
                  value={matchRules.standardSets}
                  onChange={handleMatchRulesChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="tiebreakerPoints" className="form-label">
                  Super Tiebreaker Points
                </label>
                <input
                  id="tiebreakerPoints"
                  name="tiebreakerPoints"
                  type="number"
                  min="5"
                  max="15"
                  className="form-input"
                  value={matchRules.tiebreakerPoints}
                  onChange={handleMatchRulesChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="matchDuration" className="form-label">
                  Match Duration (minutes)
                </label>
                <input
                  id="matchDuration"
                  name="matchDuration"
                  type="number"
                  min="30"
                  max="180"
                  className="form-input"
                  value={matchRules.matchDuration}
                  onChange={handleMatchRulesChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="courtCount" className="form-label">
                  Number of Courts
                </label>
                <input
                  id="courtCount"
                  name="courtCount"
                  type="number"
                  min="1"
                  max="10"
                  className="form-input"
                  value={matchRules.courtCount}
                  onChange={handleMatchRulesChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="superTiebreaker"
                  checked={matchRules.superTiebreaker}
                  onChange={handleMatchRulesChange}
                  className="mr-2"
                />
                <span className="form-label">Enable Super Tiebreaker</span>
              </label>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#1C563D' }}>
              Points System Configuration
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="winAllSets" className="form-label">
                  Win All Sets
                </label>
                <input
                  id="winAllSets"
                  name="winAllSets"
                  type="number"
                  min="0"
                  max="10"
                  className="form-input"
                  value={pointsSystem.winAllSets}
                  onChange={handlePointsSystemChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="winTwoLoseOne" className="form-label">
                  Win 2 / Lose 1
                </label>
                <input
                  id="winTwoLoseOne"
                  name="winTwoLoseOne"
                  type="number"
                  min="0"
                  max="10"
                  className="form-input"
                  value={pointsSystem.winTwoLoseOne}
                  onChange={handlePointsSystemChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="loseTwoWinOne" className="form-label">
                  Lose 2 / Win 1
                </label>
                <input
                  id="loseTwoWinOne"
                  name="loseTwoWinOne"
                  type="number"
                  min="0"
                  max="10"
                  className="form-input"
                  value={pointsSystem.loseTwoWinOne}
                  onChange={handlePointsSystemChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="loseAll" className="form-label">
                  Lose All Sets
                </label>
                <input
                  id="loseAll"
                  name="loseAll"
                  type="number"
                  min="0"
                  max="10"
                  className="form-input"
                  value={pointsSystem.loseAll}
                  onChange={handlePointsSystemChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="tiebreak" className="form-label">
                  Tiebreak Result
                </label>
                <input
                  id="tiebreak"
                  name="tiebreak"
                  type="number"
                  min="0"
                  max="10"
                  className="form-input"
                  value={pointsSystem.tiebreak}
                  onChange={handlePointsSystemChange}
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#1C563D' }}>
              Tier Rules Configuration
            </h2>
            
            {tierRules.map((rule, index) => (
              <div key={index} className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Tier Rule {index + 1}</h3>
                  {tierRules.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTierRule(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={20} />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">Tier Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={rule.tier}
                      onChange={(e) => handleTierRuleChange(index, 'tier', e.target.value)}
                      placeholder="e.g., Tier 1"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Color</label>
                    <input
                      type="color"
                      className="form-input h-10"
                      value={rule.color}
                      onChange={(e) => handleTierRuleChange(index, 'color', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Can Play Against</label>
                    <select
                      multiple
                      className="form-input"
                      value={rule.canPlay}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        handleTierRuleChange(index, 'canPlay', selected);
                      }}
                    >
                      <option value="Seed 1">Seed 1</option>
                      <option value="Seed 2">Seed 2</option>
                      <option value="Seed 3">Seed 3</option>
                      <option value="Seed 4">Seed 4</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addTierRule}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Plus size={20} />
              Add Tier Rule
            </button>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#1C563D' }}>
              Team Constraints
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="minUnder18" className="form-label">
                  Minimum Under 18 Players
                </label>
                <input
                  id="minUnder18"
                  name="minUnder18"
                  type="number"
                  min="0"
                  max="5"
                  className="form-input"
                  value={teamConstraints.minUnder18}
                  onChange={handleTeamConstraintsChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxUnder18" className="form-label">
                  Maximum Under 18 Players
                </label>
                <input
                  id="maxUnder18"
                  name="maxUnder18"
                  type="number"
                  min="0"
                  max="5"
                  className="form-input"
                  value={teamConstraints.maxUnder18}
                  onChange={handleTeamConstraintsChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="minOver40" className="form-label">
                  Minimum Over 40 Players
                </label>
                <input
                  id="minOver40"
                  name="minOver40"
                  type="number"
                  min="0"
                  max="5"
                  className="form-input"
                  value={teamConstraints.minOver40}
                  onChange={handleTeamConstraintsChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxOver40" className="form-label">
                  Maximum Over 40 Players
                </label>
                <input
                  id="maxOver40"
                  name="maxOver40"
                  type="number"
                  min="0"
                  max="5"
                  className="form-input"
                  value={teamConstraints.maxOver40}
                  onChange={handleTeamConstraintsChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="totalPlayers" className="form-label">
                  Total Players Per Team
                </label>
                <input
                  id="totalPlayers"
                  name="totalPlayers"
                  type="number"
                  min="4"
                  max="20"
                  className="form-input"
                  value={teamConstraints.totalPlayers}
                  onChange={handleTeamConstraintsChange}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate('/league')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft size={20} />
          Back to League
        </button>
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#1C563D' }}>
          Create New League
        </h1>
        <p className="text-gray-600">
          Set up a new league with custom rules and configurations
        </p>
      </motion.div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                currentStep >= step.id 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                <step.icon size={20} />
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  currentStep >= step.id ? 'text-green-600' : 'text-gray-500'
                }`}>
                  {step.title}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 h-1 mx-4 ${
                  currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        <form onSubmit={handleSubmit}>
          {renderStepContent()}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>

            <div className="flex gap-4">
              {currentStep < steps.length ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn btn-primary"
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                  style={{
                    backgroundColor: loading ? '#999' : '#1C563D',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Creating League...' : 'Create League'}
                </button>
              )}
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateLeaguePage;